<script setup lang="ts">
import { TuButton, ButtonSeverity } from "@tinyurldev/tu-ui-vue";
import { breakpointsBootstrapV5, useBreakpoints } from "@vueuse/core";

import type { Organization } from "@Modules/teams/Data/Models/OrganizationModel.ts";
import type { Domain } from "@Modules/urls/Data/Models/DomainModel";
import useTranslation from "@Composables/useTranslation";
import SettingsTopBar from "@Modules/teams/components/SettingsTopBar.vue";
import OrgSettingsContent from "@Modules/teams/components/OrgSettingsContent.vue";
import OrgDomainsTable from "@Modules/teams/components/OrgDomainsTable.vue";

//
// Setup
const { __ } = useTranslation();
const breakpoints = useBreakpoints(breakpointsBootstrapV5);
const isMobileView = breakpoints.smallerOrEqual("xl");

//
// Props
defineProps<{
	organization: Organization;
	domains: Domain[];
	loading: boolean;
}>();

//
// Emit
const emit = defineEmits<{
	(e: "add-domain"): void;
	(e: "add-subdomain"): void;
	(e: "domain-action", domain: Domain): void;
	(e: "purchase-domain"): void;
}>();
</script>

<template>
  <div
    class="org-domains-settings"
    :class="{ 'org-domains-settings--mobile': isMobileView }"
  >
    <SettingsTopBar :title="__('organization_domains_settings.top_bar_title')" />

    <OrgSettingsContent :title="__('organization_domains_settings.add_domain_section_title')">
      <div class="org-domains-settings__cards">
        <div class="org-domains-settings__card">
          <div class="org-domains-settings__card-body">
            <p class="org-domains-settings__card-title">
              {{ __('organization_domains_settings.purchase_domain_title') }}
            </p>
            <p class="org-domains-settings__card-description">
              {{ __('organization_domains_settings.purchase_domain_description') }}
            </p>
            <p class="org-domains-settings__card-example">
              {{ __('organization_domains_settings.purchase_domain_example') }}
            </p>
          </div>
          <TuButton
            class="_purchase-domain"
            :label="__('organization_domains_settings.purchase_domain_button')"
            :severity="ButtonSeverity.PRIMARY"
            is-outlined
            is-block
            @click="emit('purchase-domain')"
          />
        </div>

        <div class="org-domains-settings__card">
          <div class="org-domains-settings__card-body">
            <p class="org-domains-settings__card-title">
              {{ __('organization_domains_settings.byo_domain_title') }}
            </p>
            <p class="org-domains-settings__card-description">
              {{ __('organization_domains_settings.byo_domain_description') }}
            </p>
            <p class="org-domains-settings__card-example">
              {{ __('organization_domains_settings.byo_domain_example') }}
            </p>
          </div>
          <TuButton
            class="_add-domain"
            :label="__('organization_domains_settings.byo_domain_button')"
            :severity="ButtonSeverity.PRIMARY"
            is-outlined
            is-block
            @click="emit('add-domain')"
          />
        </div>

        <div class="org-domains-settings__card">
          <div class="org-domains-settings__card-body">
            <p class="org-domains-settings__card-title">
              {{ __('organization_domains_settings.byo_subdomain_title') }}
            </p>
            <p class="org-domains-settings__card-description">
              {{ __('organization_domains_settings.byo_subdomain_description') }}
            </p>
            <p class="org-domains-settings__card-example">
              {{ __('organization_domains_settings.byo_subdomain_example') }}
            </p>
          </div>
          <TuButton
            class="_add-subdomain"
            :label="__('organization_domains_settings.byo_subdomain_button')"
            :severity="ButtonSeverity.PRIMARY"
            is-outlined
            is-block
            @click="emit('add-subdomain')"
          />
        </div>
      </div>
    </OrgSettingsContent>

    <OrgSettingsContent
      v-if="organization.can('manage_org_domains')"
      :title="__('organization_domains_settings.manage_domains_section_title')"
    >
      <OrgDomainsTable
        :domains="domains"
        :loading="loading"
        @action="(domain) => emit('domain-action', domain)"
      />
    </OrgSettingsContent>
  </div>
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;
@use "@tinyurldev/tu-ui-vue/scss/settings/typography.scss" as typography;
@use "@tinyurldev/tu-ui-vue/scss/settings/colors.scss" as colors;
@use "@tinyurldev/tu-ui-vue/scss/settings/border.scss" as border;

.org-domains-settings {
  $self: &;

  display: flex;
  flex-flow: column;
  gap: spacing.$s;

  &__cards {
    display: flex;
    flex-wrap: wrap;
    gap: spacing.$xl spacing.$xxs;
  }

  &__card {
    display: flex;
    flex-direction: column;
    gap: spacing.$xxs;
    padding: spacing.$s;
    background-color: colors.$background-secondary;
    border: border.$primary;
    border-radius: border.$medium-radius;
    flex: 1 0 0;
    min-width: 0;
  }

  &__card-body {
    display: flex;
    flex-direction: column;
    gap: spacing.$xxs;
    flex: 1;
    color: colors.$text;
  }

  &__card-title {
    font-size: typography.$base-font-size;
    font-weight: typography.$font-weight-semi-bold;
    line-height: typography.$base-line-height;
    margin: 0;
  }

  &__card-description {
    font-size: typography.$xs-font-size;
    font-weight: typography.$font-weight-medium;
    line-height: typography.$xs-line-height;
    margin: 0;
  }

  &__card-example {
    font-size: typography.$xs-font-size;
    font-weight: typography.$font-weight-semi-bold;
    line-height: typography.$xs-line-height;
    margin: 0;
  }

  &--mobile {
    #{$self}__cards {
      flex-flow: column;
      gap: spacing.$xxs;
    }
  }
}
</style>
